import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { PageSEO } from "@/components/PageSEO";
import { SEO } from "@/lib/seo";
import { API } from "@/lib/constants";
import { BookOpen, ShoppingCart, Tablet, CheckCircle, ArrowRight, Quote, Download, Mail } from "lucide-react";

// Link Amazon (pagina prodotto: da lì si sceglie cartaceo o Kindle)
export const AMAZON_LINKS = {
  paperback: "https://amzn.eu/d/0bZF9PI2",
  kindle: ""
};

const COVER_SRC = "/libro/copertina-dai-numeri-alle-decisioni.jpg";

const FOUR_NUMBERS = [
  { title: "Il margine di contribuzione", desc: "Il margine su cui la tua impresa vive davvero, al netto di quello che ogni vendita si porta via." },
  { title: "Il punto di pareggio", desc: "Il punto in cui smetti di perdere: da quale fatturato in poi ogni euro inizia a contare." },
  { title: "Il costo di un'ora", desc: "Quanto costa davvero un'ora del tuo tempo e di chi lavora con te." },
  { title: "Il ciclo di cassa", desc: "Quanto ti costa il tempo che passa tra quando spendi e quando incassi." }
];

const BOOK_PARTS = [
  { n: "I", title: "Perché non lo sai", desc: "Il bilancio arriva tardi, il conto corrente inganna, e la domanda \"quanto hai fatturato\" è la meno utile che ci si scambi tra imprenditori." },
  { n: "II", title: "I quattro numeri", desc: "Nessuna legge ti obbliga a calcolarli, nessuna scadenza te li chiede. Sono quelli che decidono se la tua impresa regge." },
  { n: "III", title: "Le decisioni", desc: "I quattro numeri al lavoro su quattro decisioni che prima o poi arrivano sul tavolo di chiunque: il prezzo, l'assunzione, l'investimento, il debito." },
  { n: "IV", title: "Il sistema", desc: "Sapere una volta non serve quasi a niente: serve saperlo ogni mese, in un'ora, su una pagina sola." },
  { n: "V", title: "Più lontano di te", desc: "Il patrimonio, il passaggio, il valore: quello che resta quando l'impresa continua senza chi l'ha fatta nascere." }
];

const BOOK_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Book",
  "@id": "https://casalisilvia.com/libro#book",
  "name": "Dai numeri alle decisioni",
  "alternateName": "Dai numeri alle decisioni: I quattro numeri che nessuna legge ti obbliga a calcolare, e le decisioni che ne dipendono",
  "url": "https://casalisilvia.com/libro",
  "image": "https://casalisilvia.com/libro/copertina-dai-numeri-alle-decisioni.jpg",
  "inLanguage": "it",
  "numberOfPages": 238,
  "description": "I quattro numeri che nessuna legge ti obbliga a calcolare, e le decisioni che ne dipendono. Storie vere di imprese, con i conti fatti riga per riga.",
  "author": {
    "@type": "Person",
    "@id": "https://casalisilvia.com/#silvia-casali",
    "name": "Silvia Casali",
    "jobTitle": "Dottore Commercialista e Revisore Legale",
    "url": "https://casalisilvia.com/chi-siamo"
  },
  "publisher": { "@id": "https://casalisilvia.com/#organization" },
  "workExample": [
    {
      "@type": "Book",
      "@id": "https://casalisilvia.com/libro#cartacea",
      "bookEdition": "Edizione cartacea",
      "bookFormat": "https://schema.org/Paperback",
      "isbn": "9798171067694",
      "numberOfPages": 238,
      "inLanguage": "it",
      "url": "https://www.amazon.it/dp/B0HJMTQC1K",
      "offers": {
        "@type": "Offer",
        "price": "19.66",
        "priceCurrency": "EUR",
        "availability": "https://schema.org/InStock",
        "url": "https://www.amazon.it/dp/B0HJMTQC1K",
        "seller": { "@type": "Organization", "name": "Amazon.it" }
      }
    },
    {
      "@type": "Book",
      "@id": "https://casalisilvia.com/libro#kindle",
      "bookEdition": "Edizione Kindle",
      "bookFormat": "https://schema.org/EBook",
      "inLanguage": "it",
      "url": "https://www.amazon.it/dp/B0HJMTQC1K",
      "offers": {
        "@type": "Offer",
        "price": "9.99",
        "priceCurrency": "EUR",
        "availability": "https://schema.org/InStock",
        "url": "https://www.amazon.it/dp/B0HJMTQC1K",
        "seller": { "@type": "Organization", "name": "Amazon.it" }
      }
    }
  ]
};

const AmazonButtons = ({ align = "start" }) => {
  const available = AMAZON_LINKS.paperback || AMAZON_LINKS.kindle;
  if (!available) {
    return (
      <div className={`flex flex-col ${align === "center" ? "items-center" : "items-start"} gap-3`} data-testid="libro-amazon-pending">
        <div className="inline-flex items-center gap-3 border border-gold/40 bg-[#FAF6EE] text-gold px-8 py-4 rounded font-bold tracking-wide">
          <ShoppingCart size={20} />
          A BREVE DISPONIBILE SU AMAZON
        </div>
        <p className="text-[#64708A] text-sm">Versione cartacea e Kindle in arrivo. Iscriviti alla newsletter per sapere quando esce.</p>
      </div>
    );
  }
  return (
    <div className={`flex flex-col sm:flex-row gap-4 ${align === "center" ? "justify-center" : ""}`}>
      {AMAZON_LINKS.paperback && (
        <a
          href={AMAZON_LINKS.paperback}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-3 bg-gold text-black hover:bg-[#96742F] px-8 py-4 rounded font-bold tracking-wide transition-all hover:scale-105 glow-gold"
          data-testid="libro-amazon-paperback"
        >
          <ShoppingCart size={20} />
          {AMAZON_LINKS.kindle ? "CARTACEO SU AMAZON" : "ACQUISTA SU AMAZON"}
        </a>
      )}
      {AMAZON_LINKS.kindle && (
        <a
          href={AMAZON_LINKS.kindle}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-3 border-2 border-gold/60 text-gold hover:bg-gold hover:text-black px-8 py-4 rounded font-bold tracking-wide transition-all"
          data-testid="libro-amazon-kindle"
        >
          <Tablet size={20} />
          VERSIONE KINDLE
        </a>
      )}
    </div>
  );
};

const EstrattoSection = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !privacy || status === "loading") return;
    setStatus("loading");
    try {
      await axios.post(`${API}/libro/estratto`, { email, name, privacy_accepted: privacy });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="estratto" className="py-24 bg-[#FAF8F5] border-b border-gold/10" data-testid="libro-estratto-form-section">
      <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F7F0E1] border border-gold/30 mb-8">
          <Download className="text-gold" size={26} />
        </div>
        <h2 className="font-heading text-4xl md:text-5xl text-[#0D1527] mb-4">LEGGI IL PRIMO CAPITOLO</h2>
        <p className="text-[#4A5568] text-lg mb-10 leading-relaxed">
          Ricevi gratuitamente l'introduzione e il primo capitolo in PDF.<br />
          Ti iscriviamo alla newsletter con i prossimi contenuti e approfondimenti.
        </p>
        {status === "done" ? (
          <div className="bg-[#F7F0E1] border border-gold/40 rounded-lg p-8" data-testid="libro-estratto-success">
            <CheckCircle className="text-gold mx-auto mb-4" size={32} />
            <p className="text-[#0D1527] text-lg font-medium mb-2">Fatto! Controlla la tua casella email.</p>
            <p className="text-[#4A5568] text-sm mb-6">Ti abbiamo inviato il link per scaricare l'estratto. Puoi anche scaricarlo subito:</p>
            <a
              href="/libro/estratto-dai-numeri-alle-decisioni.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-gold text-black hover:bg-[#96742F] px-8 py-4 rounded font-bold tracking-wide transition-all hover:scale-105"
              data-testid="libro-estratto-download-link"
            >
              <Download size={18} /> SCARICA IL PDF
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="max-w-xl mx-auto space-y-4" data-testid="libro-estratto-form">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Il tuo nome"
                className="flex-1 bg-white border border-[#D6CFC0] focus:border-gold rounded px-4 py-4 text-[#0D1527] placeholder-[#9AA3B2] outline-none transition-colors"
                data-testid="libro-estratto-name-input"
              />
              <div className="relative flex-1">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8B94A6]" size={18} />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="La tua email"
                  className="w-full bg-white border border-[#D6CFC0] focus:border-gold rounded pl-12 pr-4 py-4 text-[#0D1527] placeholder-[#9AA3B2] outline-none transition-colors"
                  data-testid="libro-estratto-email-input"
                />
              </div>
            </div>
            <label className="flex items-start gap-3 text-left cursor-pointer group" data-testid="libro-estratto-privacy-label">
              <input
                type="checkbox"
                required
                checked={privacy}
                onChange={(e) => setPrivacy(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#c8a96e] flex-shrink-0 cursor-pointer"
                data-testid="libro-estratto-privacy-checkbox"
              />
              <span className="text-[#64708A] text-xs leading-relaxed group-hover:text-[#4A5568] transition-colors">
                Ho letto l'<Link to="/privacy-policy" className="underline text-[#4A5568] hover:text-gold" onClick={(e) => e.stopPropagation()}>informativa privacy</Link> e
                acconsento al trattamento dei miei dati per ricevere l'estratto del libro e la newsletter di SC Dottori Commercialisti.
                Posso annullare l'iscrizione in ogni momento dal link presente in ogni email. *
              </span>
            </label>
            <button
              type="submit"
              disabled={status === "loading" || !privacy}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold text-black hover:bg-[#96742F] px-10 py-4 rounded font-bold tracking-wide transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
              data-testid="libro-estratto-submit"
            >
              {status === "loading" ? "INVIO..." : "RICEVI L'ESTRATTO"}
            </button>
          </form>
        )}
        {status === "error" && (
          <p className="text-red-400 text-sm mt-4" data-testid="libro-estratto-error">Si è verificato un errore. Riprova tra qualche istante.</p>
        )}
      </div>
    </section>
  );
};

export default function LibroPage() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash === "#estratto") {
      setTimeout(() => document.getElementById("estratto")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [location.hash]);
  return (
    <main data-testid="libro-page" className="bg-[#FAF8F5]">
      <PageSEO {...SEO.libro} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(BOOK_JSONLD) }} />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20" data-testid="libro-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F4EFEA] via-[#FAF8F5] to-[#F4EFEA]" />
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-[#FAF6EE] rounded-full blur-[120px] pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-gold font-bold tracking-[0.3em] text-sm mb-6 animate-fade-in-up">
              IL LIBRO · SILVIA CASALI
            </p>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-[#0D1527] leading-tight mb-6 animate-fade-in-up stagger-1">
              DAI NUMERI<br />
              <span className="gradient-text">ALLE DECISIONI</span>
            </h1>
            <p className="text-xl md:text-2xl text-[#3B4658] mb-4 animate-fade-in-up stagger-2 leading-relaxed">
              I quattro numeri che nessuna legge ti obbliga a calcolare,<br className="hidden md:block" />
              e le decisioni che ne dipendono.
            </p>
            <p className="text-[#64708A] text-sm mb-2 animate-fade-in-up stagger-2">
              di <span className="text-gold">Silvia Casali</span> · Dottore Commercialista · Revisore dei Conti
            </p>
            <p className="text-gold/70 text-xs tracking-[0.2em] mb-10 animate-fade-in-up stagger-2" data-testid="libro-pagine">238 PAGINE · CARTACEO E KINDLE</p>
            <div className="animate-fade-in-up stagger-3">
              <AmazonButtons />
            </div>
          </div>

          {/* 3D Covers: front + back */}
          <div className="flex justify-center lg:justify-end items-center" style={{ perspective: "1200px" }}>
            <img
              src="/libro/retro-dai-numeri-alle-decisioni.jpg"
              alt="Retro di copertina del libro Dai numeri alle decisioni"
              className="w-44 md:w-52 lg:w-60 rounded shadow-2xl -mr-14 md:-mr-16 relative z-0"
              style={{ transform: "rotateY(10deg) rotate(-3deg) scale(0.94)", filter: "brightness(0.94)", boxShadow: "-16px 24px 45px rgba(13,21,39,0.25)" }}
              data-testid="libro-cover-back"
            />
            <div
              className="relative z-10 transition-transform duration-500 hover:scale-105"
              style={{ transform: "rotateY(-14deg) rotateX(2deg)", transformStyle: "preserve-3d" }}
              data-testid="libro-cover-3d"
            >
              <img
                src={COVER_SRC}
                alt="Copertina del libro Dai numeri alle decisioni di Silvia Casali"
                className="w-56 md:w-64 lg:w-80 rounded-r-md rounded-l-sm shadow-2xl"
                style={{ boxShadow: "24px 32px 60px rgba(13,21,39,0.3), -8px 0 20px rgba(179,142,70,0.15)" }}
              />
              <div
                className="absolute top-0 -left-3 h-full w-3 rounded-l-sm"
                style={{ background: "linear-gradient(90deg, #05070d, #10141f)", transform: "rotateY(-60deg) translateX(-2px)", transformOrigin: "right" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Estratto - incipit */}
      <section className="py-24 bg-[#F4EFEA] border-y border-[#E8E2D5]" data-testid="libro-estratto">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <Quote className="text-gold/40 mb-8" size={48} />
          <div className="space-y-6 text-[#3B4658] text-lg md:text-xl leading-relaxed font-light">
            <p>
              È un martedì di maggio. Sul tavolo c'è un fascicolo con la copertina azzurra, e dentro c'è il bilancio dell'anno prima.
            </p>
            <p>
              L'uomo seduto davanti a me ha cinquantadue anni, due capannoni, undici dipendenti e le mani di uno che per vent'anni ha fatto il lavoro che adesso fa fare agli altri. Ha davanti l'ultima riga del conto economico. Utile d'esercizio: 42.000 euro.
            </p>
            <p>
              La guarda per qualche secondo. Poi dice una frase che ho sentito, con parole leggermente diverse, qualche centinaio di volte.
            </p>
            <p className="font-heading text-3xl md:text-4xl text-gold py-2">«E allora dove sono?»</p>
            <p>
              Non è una domanda retorica. È una domanda vera, fatta in buona fede da una persona intelligente che ha appena scoperto di aver guadagnato 42.000 euro in un anno in cui era convinta di averci rimesso.
            </p>
            <p className="text-[#0D1527] font-medium">Questo libro nasce da lì. Non dall'ultima riga: dalla domanda.</p>
          </div>
          <p className="text-gold/60 text-sm tracking-widest mt-10">— DALL'INTRODUZIONE</p>
        </div>
      </section>

      {/* Estratto scaricabile */}
      <EstrattoSection />

      {/* I quattro numeri */}
      <section className="py-24 bg-[#F4EFEA]" data-testid="libro-quattro-numeri">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-5xl md:text-6xl text-[#0D1527] mb-4">I QUATTRO NUMERI</h2>
            <p className="text-[#4A5568] text-lg max-w-2xl mx-auto">
              Nessuna scadenza te li chiede. Nessun modello fiscale li contiene.<br />Eppure sono quelli da cui dipendono le tue decisioni.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FOUR_NUMBERS.map((n, i) => (
              <div
                key={i}
                className="group bg-[#FAF8F5] border border-[#E8E2D5] hover:border-gold/50 rounded-lg p-8 transition-all duration-300 hover:-translate-y-1"
                data-testid={`libro-numero-${i + 1}`}
              >
                <p className="font-heading text-6xl text-gold/20 group-hover:text-gold/40 transition-colors mb-4">{i + 1}</p>
                <h3 className="font-heading text-2xl text-[#0D1527] mb-3">{n.title}</h3>
                <p className="text-[#4A5568] text-sm leading-relaxed">{n.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Struttura del libro */}
      <section className="py-24 bg-[#FAF8F5]" data-testid="libro-struttura">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-5xl md:text-6xl text-[#0D1527] mb-4">CINQUE PARTI, UN PERCORSO</h2>
            <p className="text-[#4A5568] text-lg">Non un manuale: storie vere di imprese, con i conti fatti riga per riga.</p>
          </div>
          <div className="space-y-4">
            {BOOK_PARTS.map((p, i) => (
              <div
                key={i}
                className="flex items-start gap-6 bg-white border border-[#E8E2D5] hover:border-gold/30 rounded-lg p-6 md:p-8 transition-colors"
                data-testid={`libro-parte-${i + 1}`}
              >
                <span className="font-heading text-4xl md:text-5xl text-gold/60 w-14 flex-shrink-0">{p.n}</span>
                <div>
                  <h3 className="font-heading text-2xl text-[#0D1527] mb-2">{p.title}</h3>
                  <p className="text-[#4A5568] leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-[#FAF6EE] border border-gold/20 rounded-lg p-6 md:p-8 flex items-start gap-4">
            <CheckCircle className="text-gold flex-shrink-0 mt-1" size={22} />
            <p className="text-[#4A5568] leading-relaxed">
              In fondo al libro trovi <span className="text-[#0D1527] font-medium">il tuo quaderno</span>: le pagine dove calcolare i quattro numeri sulla tua impresa, non solo leggerli su quella di qualcun altro.
            </p>
          </div>
        </div>
      </section>

      {/* Autrice */}
      <section className="py-24 bg-[#F4EFEA] border-t border-[#EFE9DE]" data-testid="libro-autrice">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-[280px,1fr] gap-12 items-start">
          <div className="relative mx-auto md:mx-0 w-64 md:w-full" data-testid="libro-autrice-foto">
            <div className="absolute -inset-3 border border-gold/30 rounded-lg pointer-events-none" />
            <img
              src="/libro/silvia-casali.jpg"
              alt="Silvia Casali, Dottore Commercialista e autrice del libro Dai numeri alle decisioni"
              className="w-full rounded-lg shadow-2xl object-cover"
              style={{ boxShadow: "16px 20px 45px rgba(13,21,39,0.25)" }}
            />
          </div>
          <div>
            <p className="text-gold font-bold tracking-[0.25em] text-xs mb-4">L'AUTRICE</p>
            <h2 className="font-heading text-4xl md:text-5xl text-[#0D1527] mb-6">SILVIA CASALI</h2>
            <div className="space-y-4 text-[#4A5568] leading-relaxed">
              <p>
                Dottore Commercialista iscritta all'Ordine dei Dottori Commercialisti e degli Esperti Contabili di Ravenna (n. 511 A), Revisore Legale dei Conti, con 20 anni di esperienza al fianco di imprese piccole e piccolissime: ristoranti, alberghi, officine, negozi, studi professionali, imprese familiari alla seconda generazione.
              </p>
              <p>
                Le storie raccontate nel libro sono vere, cambiate solo nei dettagli che servivano a proteggere chi le ha raccontate. Quello che non è cambiato è la struttura dell'errore: perché è sempre la stessa, ed è quella la cosa utile.
              </p>
            </div>
            <Link
              to="/chi-siamo"
              className="inline-flex items-center gap-2 text-gold hover:text-yellow-400 font-bold text-sm tracking-wide mt-6 transition-colors"
              data-testid="libro-link-chi-siamo"
            >
              SCOPRI DI PIÙ SULL'AUTRICE <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section className="py-28 relative overflow-hidden bg-[#F4EFEA]" data-testid="libro-cta">
        <div className="absolute inset-0 bg-gradient-to-t from-gold/5 to-transparent pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <p className="font-heading text-2xl md:text-3xl text-[#3B4658] italic mb-8 leading-relaxed">
            «I numeri non servono a raccontare quello che è successo.<br />Servono a decidere quello che succederà.»
          </p>
          <h2 className="font-heading text-4xl md:text-5xl text-[#0D1527] mb-10">
            DISPONIBILE IN CARTACEO<br /><span className="gradient-text">E VERSIONE KINDLE</span>
          </h2>
          <AmazonButtons align="center" />
        </div>
      </section>
    </main>
  );
}
